import { ShoppingCart, Menu, X, LogIn, LogOut } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import BrandMark from "@/components/BrandMark";

const NAV = [
  { label: "Catalog", to: "/catalog", testid: "nav-catalog-link" },
  { label: "Lab Standards", href: "/#standards", testid: "nav-lab-standards-link" },
  { label: "Suppliers", to: "/suppliers", testid: "nav-suppliers-link" },
  { label: "Wholesale", href: "/#wholesale", testid: "nav-wholesale-link" },
  { label: "Compliance", href: "/#compliance", testid: "nav-compliance-link" },
  { label: "COA Library", to: "/coa-library", testid: "nav-coa-library-link" },
  { label: "Track Order", to: "/track", testid: "nav-track-order-link" },
];

const SPRING = { type: "spring", stiffness: 420, damping: 34 };

function NavPill({ n, active, hovered, onHover, onClick, testid }) {
  const cls = `relative px-2.5 py-1.5 rounded-full text-[13px] whitespace-nowrap font-medium transition-colors duration-200 ${
    active ? "text-slate-900 font-semibold" : "text-slate-600 hover:text-slate-900"}`;
  const inner = (
    <>
      {hovered && !active && (
        <motion.span layoutId="nav-hover" transition={SPRING} className="absolute inset-0 rounded-full bg-slate-100" />
      )}
      <span className="relative z-10">{n.label}</span>
      {active && (
        <motion.span layoutId="nav-active" transition={SPRING}
          className="absolute left-2.5 right-2.5 -bottom-[13px] h-[3px] rounded-full bg-sky-600" />
      )}
    </>
  );
  const props = { "data-testid": testid, onMouseEnter: onHover, onClick, className: cls, "data-active": active || undefined };
  return n.to ? <Link to={n.to} {...props}>{inner}</Link> : <a href={n.href} {...props}>{inner}</a>;
}

export default function Header() {
  const { count, subtotal, setDrawerOpen } = useCart();
  const { user, loading: authLoading, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const location = useLocation();
  const items = user ? [...NAV, { label: "Dashboard", to: "/admin", testid: "nav-dashboard-link" }] : NAV;
  const isActive = (n) => !!n.to && location.pathname === n.to;

  return (
    <>
      <div className="bg-[#0B111A] text-amber-400 text-center py-2 px-4" data-testid="ruo-top-banner">
        <p className="font-tech text-[11px] sm:text-xs tracking-[0.14em] uppercase">
          For laboratory &amp; in-vitro research use only — Not for human or clinical diagnostic consumption
        </p>
      </div>
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="group flex items-center gap-2.5" data-testid="nav-brand-logo">
            <motion.span whileHover={{ rotate: -6, scale: 1.05 }} transition={SPRING} className="inline-flex"><BrandMark className="h-9 w-9" /></motion.span>
            <span className="font-display font-bold text-lg tracking-tight text-slate-900">
              Arias <span className="text-sky-700">Peptides</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" onMouseLeave={() => setHovered(null)} data-testid="desktop-nav">
            {items.map((n) => (
              <NavPill key={n.testid} n={n} testid={n.testid} active={isActive(n)} hovered={hovered === n.testid} onHover={() => setHovered(n.testid)} />
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {!authLoading && (user ? (
              <div className="hidden sm:flex items-center gap-2 border border-slate-200 bg-white pl-1 pr-2 py-1 rounded-full" data-testid="nav-user-chip">
                {user.picture && <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />}
                <span className="text-xs font-medium text-slate-700 max-w-[120px] truncate">{user.name || user.email}</span>
                <button onClick={logout} data-testid="nav-signout-btn" className="text-slate-400 hover:text-red-600 transition-colors" aria-label="Sign out">
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} transition={SPRING} onClick={login} data-testid="nav-signin-btn"
                className="group relative inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-800 overflow-hidden whitespace-nowrap shrink-0">
                <span className="absolute inset-0 bg-slate-900 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                <LogIn className="relative z-10 h-4 w-4 group-hover:text-white transition-colors duration-300" />
                <span className="relative z-10 hidden sm:inline group-hover:text-white transition-colors duration-300">Sign in</span>
              </motion.button>
            ))}
            <motion.button whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.96 }} transition={SPRING}
              onClick={() => setDrawerOpen(true)} data-testid="nav-cart-button"
              className="group relative inline-flex items-center gap-2 h-10 pl-4 pr-4 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-[0_8px_24px_-12px_rgba(15,23,42,0.6)] whitespace-nowrap shrink-0">
              <span className="absolute inset-0 rounded-full overflow-hidden"><span className="absolute inset-0 bg-sky-600 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out" /></span>
              <ShoppingCart className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:-rotate-6" />
              <span className="relative z-10 hidden sm:inline">Order Request</span>
              <AnimatePresence>
                {count > 0 && (
                  <motion.span key="total" initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: "auto" }} exit={{ opacity: 0, width: 0 }}
                    className="relative z-10 hidden xl:inline font-tech text-xs text-slate-300 group-hover:text-white whitespace-nowrap overflow-hidden">
                    ${subtotal.toFixed(2)}
                  </motion.span>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {count > 0 && (
                  <motion.span key={count} data-testid="cart-badge-count" initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }} transition={SPRING}
                    className="absolute -top-1.5 -right-1.5 z-20 h-5 min-w-5 px-1 rounded-full bg-sky-500 ring-2 ring-white text-white text-[11px] font-tech flex items-center justify-center">
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            <button className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-colors" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
              className="lg:hidden overflow-hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
              <div className="px-3 py-3 flex flex-col gap-1">
                {items.map((n, i) => {
                  const cls = `flex items-center justify-between px-3 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    isActive(n) ? "bg-sky-50 text-sky-700 font-semibold" : "text-slate-700 hover:bg-slate-100"}`;
                  const content = <><span>{n.label}</span>{isActive(n) && <span className="h-1.5 w-1.5 rounded-full bg-sky-600" />}</>;
                  const props = { "data-testid": `mobile-${n.testid}`, onClick: () => setOpen(false), className: cls };
                  return (
                    <motion.div key={n.testid} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}>
                      {n.to ? <Link to={n.to} {...props}>{content}</Link> : <a href={n.href} {...props}>{content}</a>}
                    </motion.div>
                  );
                })}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
